import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  ControlContainer,
  FormGroup,
  FormGroupDirective,
} from '@angular/forms';
import { AuthentificationService } from '@app/authentification/authentification.service';
import { isSpouse } from '@app/authentification/user-identity';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, take } from 'rxjs';

export interface PrFunnelPersonsFormData {
  firstName?: string;
  lastName?: string;
  name?: string;
  origin?: string;
  type?: string;
  birthDate?: string;
  manager?: string;
  dependant?: boolean;
  handicap?: boolean;
}

@Component({
  selector: 'pr-funnel-persons',
  templateUrl: './pr-funnel-persons.component.html',
  styleUrls: ['./pr-funnel-persons.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  standalone: false,
})
export class PrFunnelPersonsComponent implements OnInit {
  @Input() formGroup!: FormGroup;
  @Input() index!: number;
  @Input() derrors: boolean = false;
  @Input() type: 'child' | 'corporate' = 'child';
  @Output() remove = new EventEmitter<number>();

  childFiliations: any[];
  corporateTypes: any[];

  form: any;

  constructor(
    private referenceDao: ReferenceDao,
    private translate: TranslateService,
    private formGroupDirective: FormGroupDirective,
    private authService: AuthentificationService,
  ) {}

  ngOnInit() {
    this.formGroupDirective.form = this.formGroup;

    if (this.type === 'child') {
      combineLatest([
        this.referenceDao.getChildFiliations(),
        this.authService.authentication.pipe(take(1)),
      ]).subscribe(([childFiliations, user]) => {
        // Pour le conjoint, « précédente union » et « précédente union du conjoint »
        // sont inversées par rapport au point de vue du back (centré client principal).
        const source = isSpouse(user)
          ? this.withSwappedPreviousUnions(childFiliations)
          : childFiliations;
        this.childFiliations = source
          .map(item => ({
            id: item.id,
            value: this.translate.instant(item.value),
          }))
          .sort((a, b) => a.value.localeCompare(b.value));
      });
    } else if (this.type === 'corporate') {
      this.corporateTypes = [
        { id: 'unknow', value: 'Inconnu' },

        { id: 'ei', value: 'E.I.' },
        { id: 'eirl', value: 'E.I.R.L.' },
        { id: 'micro_entreprise', value: 'Micro-entreprise' },
        { id: 'auto_entrepreneur', value: 'Auto-entrepreneur' },
        { id: 'agent_commercial', value: 'Agent-commercial' },

        { id: 'sa', value: 'S.A.' },
        { id: 'sas', value: 'S.A.S.' },
        { id: 'sasu', value: 'S.A.S.U.' },
        { id: 'sarl', value: 'S.A.R.L.' },
        { id: 'eurl', value: 'E.U.R.L.' },
        { id: 'snc', value: 'S.N.C.' },
        { id: 'scs', value: 'S.C.S.' },
        { id: 'sca', value: 'S.C.A.' },

        { id: 'sc', value: 'S.C.' },
        { id: 'sci', value: 'S.C.I.' },
        { id: 'scm', value: 'S.C.M.' },
        { id: 'scp', value: 'S.C.P.' },
        { id: 'scea', value: 'S.C.E.A.' },
        { id: 'gfa', value: 'G.F.A.' },

        { id: 'earl', value: 'E.A.R.L.' },
        { id: 'gaec', value: 'G.A.E.C.' },

        { id: 'selarl', value: 'S.E.L.A.R.L.' },
        { id: 'selas', value: 'S.E.L.A.S.' },
        { id: 'selafa', value: 'S.E.L.A.F.A.' },
        { id: 'selca', value: 'S.E.L.C.A.' },

        { id: 'scop', value: 'S.C.O.P.' },
        { id: 'scic', value: 'S.C.I.C.' },
        { id: 'sce', value: 'S.C.E.' },

        { id: 'association_1901', value: 'Association loi 1901' },
        {
          id: 'association_utilite_publique',
          value: "Association reconnue d'utilité publique",
        },
        { id: 'association_cultuelle', value: 'Association cultuelle' },
        { id: 'fondation', value: 'Fondation' },
        { id: 'fonds_dotation', value: 'Fonds de dotation' },

        { id: 'gie', value: 'G.I.E.' },
        { id: 'geie', value: 'G.E.I.E.' },
        { id: 'epic', value: 'E.P.I.C.' },
        { id: 'epa', value: 'E.P.A.' },
        { id: 'sem', value: 'S.E.M.' },
        { id: 'spl', value: 'S.P.L.' },
        { id: 'se', value: 'S.E.' },
      ];
    }
  }

  /**
   * Échange les codes (id) des options « précédente union » et « précédente union
   * du conjoint ». Le back enregistre la filiation du point de vue du client
   * principal ; pour le conjoint, ces deux notions sont inversées. En échangeant les
   * id (les libellés restant en place), la valeur sélectionnée puis envoyée au back
   * reste correcte dans les deux sens (lecture et écriture).
   */
  private withSwappedPreviousUnions(list: any[]): any[] {
    const previous = list.find(
      (item: any) =>
        typeof item.value === 'string' &&
        item.value.endsWith('previous_union') &&
        !item.value.endsWith('partner_previous_union'),
    );
    const partnerPrevious = list.find(
      (item: any) =>
        typeof item.value === 'string' &&
        item.value.endsWith('partner_previous_union'),
    );
    if (!previous || !partnerPrevious) {
      return list;
    }
    return list.map((item: any) => {
      if (item === previous) {
        return { ...item, id: partnerPrevious.id };
      }
      if (item === partnerPrevious) {
        return { ...item, id: previous.id };
      }
      return item;
    });
  }

  delete() {
    this.remove.emit(this.index);
  }
}
